import React from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import keaiRoundLogo from '../../assets/keai_violet.png';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

import { useEffect, useState, useRef } from 'react';
import ChatMessages from './ChatMessages';
import useFileUpload from '../hooks/useFileUpload';
import { Description, FilePresent, PictureAsPdf } from '@mui/icons-material';

const Chat: React.FC = () => {
  const isLeftSectionOpen = useSelector((state: RootState) => state.section.leftOpen);
  const isRightSectionOpen = useSelector((state: RootState) => state.section.rightOpen);
  const messages = useSelector((state: RootState) => state.chat.messages);
  const showLogo = useSelector((state: RootState) => state.chat.showLogo);
  const theme = useTheme();
  const hasMessages = messages.length > 0;
  const [showText, setShowText] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isDragging, handleFileDrop, handleDragEnter, handleDragLeave, handleDragOver } = useFileUpload();
  const [dragCounter, setDragCounter] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowText(true);
    }, 1000); 
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log('showText state:', showText); 
  }, [showText]);

  const handleDragEnterLocal = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    handleDragEnter(e);
  };

  const handleDragLeaveLocal = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => Math.max(0, prev - 1));
    if (dragCounter <= 1) {
      handleDragLeave(e);
    }
  };

  const handleDropLocal = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(0);
    handleFileDrop(e);
  };

  const themeMode = useSelector((state: RootState) => state.theme.mode);
const backgroundMode = useSelector((state: RootState) => state.background.mode);

const getBackgroundColor = () => {
  if (backgroundMode === 'none') {
    if (themeMode === 'dark') return '#232323';      // dark mode color
    if (themeMode === 'light') return '#fff';        // light mode color
    if (themeMode === 'blue') return '#F3F6FA';      // main chat bg for blue mode
  }
  return undefined; // use default/image when ON
};


  const isActuallyDragging = isDragging || dragCounter > 0;

  return (
    <Box
      data-drop-zone="true"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      onDrop={handleDropLocal}
      onDragEnter={handleDragEnterLocal}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeaveLocal}
      sx={{
        overflow: 'hidden',
        position: 'relative',
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: '100%',
        padding: '0px',
        boxSizing: 'border-box',
        marginLeft: isLeftSectionOpen ? '10vw' : '8vw',
        background: getBackgroundColor(), 
        marginRight: isRightSectionOpen ? '10vw' : '10vw',
      }}
    >
      {/* Drag and Drop Overlay */}
      {isActuallyDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            
             backgroundColor: alpha(theme.palette.background.paper, 0.1),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)',
          }}
        >

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 4,
              borderRadius: 3,
              maxWidth: 450,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                mb: 2,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/* PDF File */}
              <Box
                sx={{
                transform: 'translateX(15px) translateY(0px) rotate(-10deg)',
                }}
              >
                <PictureAsPdf sx={{ color: '#E53935', fontSize: 62 }} />
              </Box>
              {/* Link */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FilePresent sx={{ color: '#1976D2', fontSize: 62 }} />
              </Box>
              {/* DOCX File */}
              <Box
                sx={{
                  transform: 'translateX(-15px) translateY(0px) rotate(10deg)',
                }}
              >
                <Description sx={{ color: '#1565C0', fontSize: 62 }} />
              </Box>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 'bold',
                mb: 1,
                fontSize: '1.5rem',
              }}
            >
              Drop anything
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '1rem',
                opacity: 0.9,
              }}
            >
              Support .pdf, .txt, .docx, .csv and Urls
            </Typography>
          </Box>
        </Box>
       )}

      {!hasMessages && showLogo && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          position="absolute"
          top="0%"
          left="0%"
          right="0%"
          bottom="15%"
          alignItems="center"
          flexGrow={1}
          sx={{
            textAlign: 'center',
            transition: 'transform 0.3s ease',
            padding: '0',
            boxSizing: 'border-box',
          }}
        >
          <motion.img
            src={keaiRoundLogo}
            alt="Keai Round Logo"
            style={{ width: '50px', height: '50px', display: 'inline-block' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 11, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}
          />
          <Typography
            variant="h5"
            color="textSecondary"
            mt={2}
            sx={{
              transition: 'transform 0.6s ease-in-out, color 0.6s ease-in-out',
              transform: isHovered ? 'scale(1.05) rotate(-2deg)' : 'scale(1) rotate(0)',
            color: isHovered ? theme.palette.text.primary : theme.palette.text.secondary,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            RegenAI
          </Typography>

        </Box>
      )}
      {hasMessages && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            width: '100%',
            marginLeft: '0px',
            transition: 'transform 0.3s ease',
            flex: 1,
            overflowY: 'auto',
          }}
        >
          <ChatMessages />
        </Box>
      )}
    </Box>
  );
};

export default Chat;
